import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Loader2, Trash2, Search, AlertTriangle, ExternalLink, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  rejected: 'bg-rose-100 text-rose-800 border-rose-300',
  null: 'bg-slate-100 text-slate-700 border-slate-300',
};

export default function AdminProductManager() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('any');
  const [deleting, setDeleting] = useState({});
  const [updatingApproval, setUpdatingApproval] = useState({});

  const authHeaders = () => {
    const sess = localStorage.getItem('token');
    return sess ? { Authorization: `Bearer ${sess}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'any') params.set('status', statusFilter);
      const res = await axios.get(
        `${API_URL}/api/admin/products/all?${params.toString()}`,
        { headers: authHeaders() }
      );
      setProducts(res.data.products || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (p) => {
    const ok = window.confirm(
      `Delete "${p.name}" permanently?\n\n` +
      `Creator: ${p._creator_store_name || '?'}\n` +
      `Product ID: ${p.product_id}\n\n` +
      `This removes the product + its delivery files. Orders/reviews referencing it are kept for history.`
    );
    if (!ok) return;
    setDeleting((d) => ({ ...d, [p.product_id]: true }));
    try {
      await axios.delete(`${API_URL}/api/products/${p.product_id}`, { headers: authHeaders() });
      toast.success(`Deleted "${p.name}"`);
      setProducts((arr) => arr.filter((x) => x.product_id !== p.product_id));
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setDeleting((d) => ({ ...d, [p.product_id]: false }));
    }
  };

  const handleSetApproval = async (p, nextStatus) => {
    const currentStatus = p.approval_status || 'null';
    if (currentStatus === nextStatus) return;

    if (nextStatus === 'rejected') {
      const ok = window.confirm(
        `Reject "${p.name}"?\n\n` +
        `Creator: ${p._creator_store_name || '?'}\n\n` +
        `This hides the product from the public marketplace. You can re-approve it later.`
      );
      if (!ok) return;
    }

    setUpdatingApproval((d) => ({ ...d, [p.product_id]: nextStatus }));
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/products/${p.product_id}/set-approval`,
        { status: nextStatus },
        { headers: authHeaders() }
      );
      const updated = res.data?.product;
      setProducts((arr) => arr.map((x) => (x.product_id === p.product_id ? { ...x, ...updated } : x)));
      toast.success(`"${p.name}" → ${nextStatus}`);
    } catch (e) {
      toast.error(`Update failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUpdatingApproval((d) => ({ ...d, [p.product_id]: null }));
    }
  };

  const counts = useMemo(() => {
    const c = { total: products.length, approved: 0, pending: 0, rejected: 0, null: 0 };
    products.forEach((p) => {
      const s = p.approval_status || 'null';
      c[s] = (c[s] || 0) + 1;
    });
    return c;
  }, [products]);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="admin-product-manager">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Trash2 className="w-7 h-7 text-slate-500" />
            Product Manager
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Search every product on the platform (regardless of approval status) and delete any one with one click.
            Admins can delete any product; deletions don't cascade to orders/reviews so historical records stay intact.
          </p>
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-slate-200 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3" data-testid="filter-bar">
          <div className="relative basis-full sm:basis-auto sm:flex-1 sm:min-w-[260px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by product name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400"
              data-testid="search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-slate-300 rounded text-sm bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
            data-testid="status-filter"
          >
            <option value="any">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
          <Button onClick={load} size="sm" variant="outline" data-testid="reload-btn">Reload</Button>
        </div>

        {/* Counts */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-center text-xs">
          {[
            { k: 'total', label: 'Total', color: 'bg-slate-100 text-slate-800' },
            { k: 'approved', label: 'Approved', color: 'bg-emerald-100 text-emerald-800' },
            { k: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-800' },
            { k: 'rejected', label: 'Rejected', color: 'bg-rose-100 text-rose-800' },
            { k: 'null', label: 'No status', color: 'bg-slate-100 text-slate-600' },
          ].map((b) => (
            <div key={b.k} className={`${b.color} rounded p-2 font-bold`}>
              <div className="text-lg">{counts[b.k] ?? 0}</div>
              <div className="opacity-70">{b.label}</div>
            </div>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-slate-300 rounded-lg p-12 text-center bg-white text-slate-500">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
            No products match the current filter.
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Name</th>
                  <th className="text-left px-3 py-2">Creator</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Price</th>
                  <th className="text-right px-3 py-2">Sales</th>
                  <th className="text-right px-3 py-2">Created</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => {
                  const status = p.approval_status || 'null';
                  return (
                    <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`product-row-${p.product_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold text-slate-900">{p.name}</div>
                        <div className="text-[10px] text-slate-400 font-mono">{p.slug || p.product_id}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-slate-700">{p._creator_store_name}</div>
                        {p._creator_store_slug && (
                          <a href={`/store/${p._creator_store_slug}`} target="_blank" rel="noreferrer"
                             className="text-[10px] text-slate-400 hover:text-slate-700 inline-flex items-center gap-0.5">
                            /store/{p._creator_store_slug} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded border ${STATUS_COLOR[status]}`}>
                          {status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700">
                        ${p.price ?? '—'}
                        {p.is_subscription && <span className="text-[10px] text-slate-400">/mo</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-600">{p.total_sales ?? 0}</td>
                      <td className="px-3 py-2 text-right text-[10px] text-slate-500">{(p.created_at || '').slice(0, 10)}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex flex-wrap gap-1 justify-end">
                          {status !== 'approved' && (
                            <Button
                              onClick={() => handleSetApproval(p, 'approved')}
                              disabled={!!updatingApproval[p.product_id] || deleting[p.product_id]}
                              size="sm"
                              className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`approve-product-${p.product_id}`}
                            >
                              {updatingApproval[p.product_id] === 'approved' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <><CheckCircle className="w-3 h-3 mr-1" /> Approve</>
                              )}
                            </Button>
                          )}
                          {status !== 'rejected' && (
                            <Button
                              onClick={() => handleSetApproval(p, 'rejected')}
                              disabled={!!updatingApproval[p.product_id] || deleting[p.product_id]}
                              size="sm"
                              variant="outline"
                              className="text-xs h-7 border-amber-400 text-amber-700 hover:bg-amber-50"
                              data-testid={`reject-product-${p.product_id}`}
                            >
                              {updatingApproval[p.product_id] === 'rejected' ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" /> Reject</>
                              )}
                            </Button>
                          )}
                          <Button
                            onClick={() => handleDelete(p)}
                            disabled={deleting[p.product_id] || !!updatingApproval[p.product_id]}
                            size="sm"
                            variant="destructive"
                            className="text-xs h-7"
                            data-testid={`delete-product-${p.product_id}`}
                          >
                            {deleting[p.product_id] ? <Loader2 className="w-3 h-3 animate-spin" /> : (
                              <><Trash2 className="w-3 h-3 mr-1" /> Delete</>
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
